import { DayHour, MinuteNumber, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { Geofence, ThingShadowState } from '../model'
import { TimeBucketUnits } from '../util/time'

export type OutcomeType = 'absoluteFail' | 'fail' | 'pass'

export enum Outcome {
  'PASS' = 'pass',
  'FAIL' = 'fail',
  'INDETERMINATE' = 'indeterminate',
}

export type RuleDecisionType<T extends BooleanRuleType | MetricRuleType | GeofenceRuleType> = {
  id: string
  type: string
  rule: T
  outcome?: Outcome
  outcomeReason?: string
}

export type BooleanRuleDecisionType = RuleDecisionType<BooleanRuleType> & {
  value?: boolean
}

export type MetricRuleDecisionType = RuleDecisionType<MetricRuleType> & {
  violatesMax?: boolean
  violatesMin?: boolean
  violatesSD?: boolean
  value?: number
  period?: number
  unit?: TimeBucketUnits
}

/** @deprecated according to RSE, should not exist in LSC */
export type GeofenceRuleDecisionType = RuleDecisionType<GeofenceRuleType> & {
  geofence: Geofence
  inArea?: boolean
}

export type AlertType = {
  thing?: ValidBusinessObject
  shadow?: ThingShadowState
  timestamp?: number
  summary?: string
  alerts?: {
    [k in OutcomeType]: (BooleanRuleDecisionType | MetricRuleDecisionType | GeofenceRuleDecisionType)[]
  }
  decisionState?: { [k: string]: OutcomeType }
  _state?: string
}

/** @deprecated */
export enum RulesetContent {
  GEOFENCE = 'GEOFENCE',
  METRIC = 'METRIC',
  GEOFENCE_AND_METRIC = 'GEOFENCE_AND_METRIC',
}

/** @deprecated according to RSE, should not exist in LSC */
export type GeofenceRuleType = {
  enabled?: boolean
  allowed?: boolean
  geofence?: ValidBusinessObject
} & ValidBusinessObject

export type MetricRuleThresholdType = {
  /** Mui / the UI / DynamoDB? can set it to `NaN` and/or `null` */
  value?: number | null
  /** @deprecated in favour of {@link RulesetType.period} */
  datapoints?: number
  /** @deprecated in favour of {@link RulesetType.period}, to be removed */
  period?: number
  /** @deprecated in favour of {@link RulesetType.unit}, to be removed */
  unit?: TimeBucketUnits
}

export type MetricRuleType = {
  name: string
  enabled: boolean
  /** @deprecated use {@link RulesetType.messageTemplate} instead */
  failMessage?: string
  deviceEnabledType: string
  key: string
  valueType: 'bucketed' | 'latest'
  max: MetricRuleThresholdType
  min: MetricRuleThresholdType
  stdDev: MetricRuleThresholdType
}

export type BooleanRuleType = {
  name: string
  enabled: boolean
  deviceEnabledType: string
  key: string
  /** the value to trigger the rule on, e.g. if set to `false`, the rule will **fail** when the {@link BooleanRuleType.key} is `false` */
  triggeringValue: boolean
}

export type RulesetDefintionType = {
  BooleanRule?: BooleanRuleType[]
  MetricRule?: MetricRuleType[]
  /** @deprecated according to RSE, should not exist in LSC */
  GeofenceRule?: GeofenceRuleType[]
}

export enum TemplatePlaceholders {
  Customer = '[Customer]',
  Ruleset = '[Ruleset]',
  Timestamp = '[Timestamp]',
  Type = '[Type]',
  Name = '[Name]',
  Group = '[Group]',
  Rule = '[Rule]',
  Attribute = '[Attribute]',
  Value = '[Value]',
  Value_Percent = '[Value Percent]',
  Max = '[Max]',
  Max_Percent = '[Max Percent]',
  Min = '[Min]',
  Min_Percent = '[Min Percent]',
  Standard_Deviation = '[Standard Deviation]',
  Period = '[Period]',
  Unit = '[Unit]',
}

export enum EscalationTemplatePlaceholders {
  Notification_Person = '[Notification Person]',
  Notification_Time = '[Notification Time]',
  Escalation_Period = '[Escalation Period]',
  Escalation_Person = '[Escalation Person]',
}

export type Template = (TemplatePlaceholders | string)[]

export interface RulesetType  {
    /**
     * Whether this ruleset is 'global'
     * 
     * A true global ruleset will be attached to the venture's seed customer
     * 
     * An LSC mechanism will make copies of the global ruleset(s) and attach them to the individual customer(s)  
     * These copies will still have the {@link global} attribute
     * */
    global?: boolean
    /** @deprecated serves no meaningful use, fetch valid types from `DeviceEnabledTypesMap` instead */
    possibleAppliesToTypes: string[],
    /** @deprecated serves no meaningful use, should infer content type from the contents of the {@link definition} */
    rulesetContent: RulesetContent
    definition: RulesetDefintionType
    /** entity type to trigger processing of this ruleset */
    appliesToType: string | undefined,
    /** apply to all entities of {@link appliesToType} */
    globallyApplyToObjectTypeId: boolean
    validBetween?: { from: { hour: DayHour, minute: MinuteNumber }, to: { hour: DayHour, minute: MinuteNumber } }
    /** controls whether all rules are processed individually (`'OR'`), or processed as a group when triggering an incident (`'AND'`) */
    setOperation?: 'AND' | 'OR'
    messageTemplate?: Template
    escalationTemplate?: Template
    /** number of {@link unit} the ruleset uses for processing */
    period?: number | undefined
    /** time period used for {@link period} */
    unit?: TimeBucketUnits | undefined
}
